import React from 'react'

import { useDecision } from '@optimizely/react-sdk'
import Loading from 'Loading'
import { useDispatch, useSelector } from 'react-redux'

import { MyGoustoPage } from '@features/my-gousto-page'
import { MyHomePage } from '@features/my-home-page'

import { actionTypes } from 'actions/actionTypes'
import actions from 'actions/user'
import { getUserSubscriptionState } from 'selectors/user'

import { ReferAFriendContainer } from './ReferAFriendContainer'
import { GoustoAnytimeModal } from './experiments/GoustoAnytime/GoustoAnytimeModal'
import { useGoustoAnytimeExperiment } from './experiments/GoustoAnytime/useGoustoAnytimeExperiment'

type MyGoustoPageProps = {
  dispatchCancelOrder: (orderId: string) => void
  dispatchRefreshOrdersState: () => void
}

const MyGoustoControlView = ({
  dispatchCancelOrder,
  dispatchRefreshOrdersState,
}: MyGoustoPageProps) => (
  <>
    {/*
      Keep this selector at the route level so app and Cypress tests can
      target the control branch without changing @features/my-gousto-page.
      The feature-modules CI job runs `yarn workspaces foreach` over changed
      `@features/*` workspaces since the branch point, so touching that
      workspace would make its isolated unit suite run as well. That is
      undesirable here because this route-only change would then inherit
      unrelated feature-workspace test/dependency failures.
    */}
    <div data-testid="my-gousto-page">
      <MyGoustoPage
        dispatchCancelOrder={dispatchCancelOrder}
        dispatchRefreshOrdersState={dispatchRefreshOrdersState}
      />
    </div>
    <ReferAFriendContainer />
  </>
)

const MyGoustoLoadingView = () => (
  <div
    aria-label="Loading My Gousto"
    data-testid="my-gousto-loading"
    role="status"
    style={{
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      minHeight: '350px',
    }}
  >
    <Loading />
  </div>
)

const ActiveSubscriptionMyGousto = ({
  dispatchCancelOrder,
  dispatchRefreshOrdersState,
}: MyGoustoPageProps) => {
  const [decision, isDecisionReady, hasDecisionTimedOut] = useDecision(
    'rockets_my_gousto_new_home_page_experiment',
  )

  if (!isDecisionReady && !hasDecisionTimedOut) {
    return <MyGoustoLoadingView />
  }

  if (decision.variationKey === 't1') {
    return (
      <div data-testid="my-home-page">
        <MyHomePage />
      </div>
    )
  }

  return (
    <MyGoustoControlView
      dispatchCancelOrder={dispatchCancelOrder}
      dispatchRefreshOrdersState={dispatchRefreshOrdersState}
    />
  )
}

export const MyGousto = () => {
  const { showModal, data, dismiss } = useGoustoAnytimeExperiment()
  const dispatch = useDispatch()
  const userSubscriptionState = useSelector(getUserSubscriptionState)
  const isUserSubscriptionLoading = userSubscriptionState === undefined
  const dispatchCancelOrder = (orderId: string) => {
    dispatch({
      type: actionTypes.ORDER_CANCEL,
      orderId,
      restorable: false,
    })
  }
  const dispatchRefreshOrdersState = () => {
    dispatch(actions.userLoadOrdersForMyGousto(true))
  }

  return (
    <div>
      {showModal && data && <GoustoAnytimeModal data={data} onDismiss={dismiss} />}
      {isUserSubscriptionLoading ? (
        <MyGoustoLoadingView />
      ) : userSubscriptionState !== 'active' ? (
        <MyGoustoControlView
          dispatchCancelOrder={dispatchCancelOrder}
          dispatchRefreshOrdersState={dispatchRefreshOrdersState}
        />
      ) : (
        <ActiveSubscriptionMyGousto
          dispatchCancelOrder={dispatchCancelOrder}
          dispatchRefreshOrdersState={dispatchRefreshOrdersState}
        />
      )}
    </div>
  )
}
